import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { Button, ScrollShadow, Tooltip } from '@heroui/react'
import type { ModalComponentProps } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useGlobalFileUpload } from '../../hooks/useGlobalFileUpload'

export interface FailedFile {
  name: string
  path: string
  reason?: 'upload' | 'encryption' | 'deleted_destination' | 'zero_bytes'
}

export interface FolderFailuresModalData {
  folderName: string
  folderPath?: string
  folderId?: string
  failedFiles: FailedFile[]
  uploadFailures: number
  encryptionFailures: number
  hasRetryableFiles: boolean // [RETRY_FAILED] Whether any files can be retried
}
function downloadFailedFilesList(folderName: string, failedFiles: FailedFile[]) {
  const header = `Failed files from ${folderName} upload:\nTotal failures: ${failedFiles.length}\n\n`

  const fileList = failedFiles.map(file => file.path).join('\n')

  const content = header + fileList

  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = `${folderName}_failed_files.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export default function FolderFailuresModal({ data }: ModalComponentProps) {
  const { setToggleModel } = useActiveModelStore()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const navigate = useNavigate()
  const handleRetryFailedFiles = useGlobalFileUpload(state => state.handleRetryFailedFiles)

  const modalData = data as FolderFailuresModalData

  if (!modalData || !modalData.failedFiles) {
    return null
  }

  const { folderName, folderPath, folderId, failedFiles, hasRetryableFiles } = modalData

  const handleDownload = () => {
    setIsDownloading(true)
    try {
      downloadFailedFilesList(folderName, failedFiles)
    } finally {
      setTimeout(() => setIsDownloading(false), 500)
    }
  }

  // [RETRY_FAILED] Retry all retryable failed files
  const handleRetry = async () => {
    if (!folderId) return

    setIsRetrying(true)
    setToggleModel(false) // Close modal

    try {
      await handleRetryFailedFiles(folderId)
    } catch (error) {
      console.error('[RETRY_FAILED] Retry error:', error)
    } finally {
      setIsRetrying(false)
    }
  }

  const handleGoToFolder = () => {
    setToggleModel(false)
    if (folderId) {
      navigate(`/folders/${folderId}`)
    } else if (folderPath) {
      navigate(folderPath)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <p className="text-lg text-black dark:text-white mt-4">
        {failedFiles.length} file{failedFiles.length !== 1 ? 's' : ''} failed to process
      </p>

      {/* File List with Infinite Scroll */}
      <ScrollShadow hideScrollBar className="flex-1 my-4 max-h-[400px]">
        <div className="space-y-1.5">
          {failedFiles.map((file, index) => (
            <div className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-default-100 dark:hover:bg-default-50 transition-colors group">
              <Tooltip
                key={index}
                className="max-w-md break-all"
                content={file.path}
                delay={500}
                placement="top"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                  {file.name}
                </span>
              </Tooltip>
            </div>
          ))}
        </div>
      </ScrollShadow>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-default-200 dark:border-default-100">
        <Button
          isLoading={isDownloading}
          radius="sm"
          startContent={<ArrowDownTrayIcon className="w-4 h-4" />}
          variant="flat"
          onPress={handleDownload}
        >
          Download list
        </Button>
        {/* [RETRY_FAILED] Retry button - only enabled when retryable files exist */}
        <Button
          color="warning"
          isDisabled={!hasRetryableFiles}
          isLoading={isRetrying}
          radius="sm"
          startContent={<ArrowPathIcon className="w-4 h-4" />}
          variant="flat"
          onPress={handleRetry}
        >
          Retry Failed
        </Button>
        <Button color="primary" radius="sm" onPress={handleGoToFolder}>
          Go to folder
        </Button>
      </div>
    </div>
  )
}
